import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChecklistModel } from '../interface/checklist-model';
import { environment } from '../../environments/environment';
import { first, firstValueFrom } from 'rxjs';
import { ImageConverter } from '../class/image-converter';
import { DataService } from './data.service';
import { DisableOrActive } from '../interface/disableOrActive';
import { StorageUrlService } from './storage-url.service';

@Injectable({
  providedIn: 'root'
})
export class ChecklistModelService {

  private _isFromMatrix = true;


  constructor(
    private _http: HttpClient,
    private dataService: DataService,
    private _storageUrlService: StorageUrlService,
  ) { }

  public async get(id: string): Promise<ChecklistModel> {
    const url = `${environment.mkgoURL}/api/v1/checklist-model/${id}`;
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp: any = await this._http.get(url, options).pipe(first()).toPromise()
    return resp as ChecklistModel;
  }

  public async getAll(args?: {
    model?: string,
    group?: string
  }): Promise<ChecklistModel[]> {
    const url = `${environment.mkgoURL}/api/v1/checklist-model`;
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    if (args) {
      options['params'] = new HttpParams({
        fromObject: args
      })
    }
    const resp: {
      checklistModels: Array<any>
    } = await this._http.get(url, options).pipe(first()).toPromise() as any;
    const chModels = resp.checklistModels.map(apiModel => new ChecklistModel(apiModel, this.dataService?.company?.checklistSim ? true : false))
    return chModels;
  }

  public async create(checklistModel: ChecklistModel, companyId: string): Promise<any> {
    const url = `${environment.mkgoURL}/api/v1/checklist-model`;
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp: { id: string } = await this._http.post(url, checklistModel, options).pipe(first()).toPromise() as any
    try {
      if (checklistModel.url && checklistModel.url.includes('blob')) {
        const blob = await ImageConverter.urlToBlob(checklistModel.url);
        const httpsUrl = await this.saveBreakdownImage(companyId, resp.id, blob);
        checklistModel.url = httpsUrl;
        await this._http.put(url, checklistModel, options).pipe(first()).toPromise()
      }
    } catch (error) {
      console.error(error);
    }
    return resp;
  }

  public async update(checklistModel: ChecklistModel, companyId: string): Promise<any> {
    const url = `${environment.mkgoURL}/api/v1/checklist-model/${checklistModel._id}`;
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp: { id: string } = await this._http.put(url, checklistModel, options).pipe(first()).toPromise() as any;
    try {
      if (resp.id && checklistModel.url && checklistModel.url.includes('blob')) {
        const blob = await ImageConverter.urlToBlob(checklistModel.url);
        const httpsUrl = await this.saveBreakdownImage(companyId, resp.id, blob);
        checklistModel.url = httpsUrl;
        await this._http.put(url, checklistModel, options).pipe(first()).toPromise()
      }
    } catch (error) {
      console.error(error);
    }
    return resp;
  }

  public async delete(id: string, _companyId?: string): Promise<any> {
    const url = `${environment.mkgoURL}/api/v1/checklist-model/${id}`;
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    return this._http.delete(url, options).pipe(first()).toPromise();
  }

  private async saveBreakdownImage(companyId: string, checklistModelId: string, data: Blob): Promise<string> {
    const resp = await this._storageUrlService.upload(data, {
      scope: 'checklist-model-default-breakdown',
      companyId,
      checklistModelId,
    });
    return resp.key;
  }

  public async getGroupedSeries(): Promise<{
    name: string;
    models: ChecklistModel[]
  }[]> {
    const allModels = await this.getAll();
    const serieMap = new Map<string, ChecklistModel[]>();
    for (const model of allModels) {
      const serieModels = serieMap.get(model.group) || [];
      serieModels.push(model);
      serieMap.set(model.group, serieModels);
    }
    const resp: {
      name: string;
      models: ChecklistModel[]
    }[] = [];
    for (const key of serieMap.keys()) {
      resp.push({
        name: key,
        models: serieMap.get(key)
      });
    }
    return resp;
  }

  public async getInactives(args?: {
    model?: string,
    group?: string
  }): Promise<ChecklistModel[]> {
    const url = `${environment.mkgoURL}/api/v1/checklist-model/inactives`;
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    if (args) {
      options['params'] = new HttpParams({
        fromObject: args
      })
    }
    const resp: {
      checklistModels: Array<any>
    } = await this._http.get(url, options).pipe(first()).toPromise() as any;
    const chModels = resp.checklistModels.map(apiModel => new ChecklistModel(apiModel, this.dataService.company.checklistSim ?? true))
    return chModels;
  }

  public async inactiveOrActive(id: string, action: DisableOrActive): Promise<ChecklistModel[]> {
    const url = `${environment.mkgoURL}/api/v1/checklist-model/${id}/${action}`;
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp = await firstValueFrom(this._http.put(url, {}, options));
    return resp["checklistModels"];
  }
}
